// MessageMeta.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  fromUnixTime,
  intervalToDuration,
  differenceInHours,
  differenceInMilliseconds,
} from 'date-fns';
import { withTranslation } from 'react-i18next';

import { TranslationNamespace } from '../resource/translationNamespace.js';
import Tooltip from '../component/Tooltip.jsx';
import Animator from '../component/Animator.jsx';
import { textColor } from '../style/variables.js';
import media from '../style/media.js';

import TimerIcon from '../../img/ic-timer-grey.svg';
import LikeIcon from '../../img/ic-like-grey.svg';
import UnlockIcon from '../../img/ic-unlock-grey.svg';
import ActiveLikeIcon from '../../img/btn_rating_like_active.svg';
import ActiveDislikeIcon from '../../img/btn_rating_unlike_active.svg';
import ViewsIcon from '../../img/ic_streaming_view_xs.svg';

/**
 * Format the number
 * @param {number} num - the number will be formatted
 * @return {string} the formatted string
 */
const formatNumber = num => (num < 0 ? '--' : num < 10 ? `0${num}` : `${num}`);

export class MessageMeta extends React.PureComponent {
  render() {
    const {
      t,
      unlockCount,
      likePercentageWithSignOrDefault,
      viewCount,
      tipPosition,
      showUnlocks,
      showLikes,
      showViewCount,
      showLikesText,
      showViewCountText,
      showUnlockCountText,
      likes,
    } = this.props;

    return (
      <StyledMessageMeta>
        {this.renderRemainingTime()}
        {showLikesText && (
          <InfoBlockWrapper>
            {' '}
            {t('likes', {
              ['0']: likes,
              ns: TranslationNamespace.POST,
            })}
          </InfoBlockWrapper>
        )}
        {showViewCountText && (
          <InfoBlockWrapper>
            {t('view_count_format', {
              ['0']: viewCount,
              ns: TranslationNamespace.POST,
            })}
          </InfoBlockWrapper>
        )}
        {showUnlockCountText && (
          <InfoBlockWrapper>
            {t('unlocked', {
              ['0']: unlockCount,
              ns: TranslationNamespace.POST,
            })}
          </InfoBlockWrapper>
        )}
        {showUnlocks && (
          <InfoBlockWrapper>
            <Tooltip
              trigger="hover"
              position={tipPosition}
              renderHint={this.renderUnlockHint}
            >
              <InfoBlock data-value={unlockCount}>
                <img src={UnlockIcon} alt="amount of unlocks" />
              </InfoBlock>
            </Tooltip>
          </InfoBlockWrapper>
        )}
        {showViewCount && (
          <InfoBlockWrapper>
            <InfoBlock data-value={viewCount}>
              <img src={ViewsIcon} alt="amount of views" />
            </InfoBlock>
          </InfoBlockWrapper>
        )}
        {showLikes && (
          <InfoBlockWrapper>
            <Tooltip
              trigger="hover"
              position={tipPosition}
              renderHint={this.renderRatingHint}
            >
              <InfoBlock data-value={likePercentageWithSignOrDefault}>
                <img src={LikeIcon} alt="likes ratio" />
              </InfoBlock>
            </Tooltip>
          </InfoBlockWrapper>
        )}
      </StyledMessageMeta>
    );
  }
  renderUnlockHint = () => {
    const { t } = this.props;

    return (
      <TooltipContentWrapper>
        {t('message_tip_unlock', {
          ns: TranslationNamespace.POST,
        })}
      </TooltipContentWrapper>
    );
  };

  renderRemainingTime() {
    const { tipPosition, expiresAtUnix, showExpires, isPublished, ttl, t } =
      this.props;
    const hasExpire = isPublished ? expiresAtUnix != null : ttl != null;

    if (!showExpires || !hasExpire) {
      return null;
    }

    return (
      <InfoBlockWrapper>
        <Tooltip
          trigger="hover"
          position={tipPosition}
          renderHint={() => (
            <TooltipContentWrapper>
              {t('message_tip_remaining_time', {
                ns: TranslationNamespace.POST,
              })}
            </TooltipContentWrapper>
          )}
        >
          {isPublished ? (
            <Animator fps={2}>
              <RemainingTime expiresAtUnix={expiresAtUnix} />
            </Animator>
          ) : (
            <RemainingTime now={0} expiresAtUnix={ttl} />
          )}
        </Tooltip>
      </InfoBlockWrapper>
    );
  }

  renderRatingHint = () => {
    const { likes, dislikes, likePercentage } = this.props;

    return (
      <TooltipContentWrapper>
        <RatingHint>
          <RatingProgressBar>
            <RatingProgressValue percentage={likePercentage} />
          </RatingProgressBar>
          <RatingHintInfo>
            <RatingHintCount>
              <img src={ActiveDislikeIcon} alt="amount of dislikes" />
              <span>{dislikes}</span>
            </RatingHintCount>
            <RatingHintCount isReverse>
              <img src={ActiveLikeIcon} alt="amount of likes" />
              <span>{likes}</span>
            </RatingHintCount>
          </RatingHintInfo>
        </RatingHint>
      </TooltipContentWrapper>
    );
  };
}

MessageMeta.propTypes = {
  showExpires: PropTypes.bool,
  showLikes: PropTypes.bool,
  showUnlocks: PropTypes.bool,
  showViewCount: PropTypes.bool,
  showLikesText: PropTypes.bool,
  showViewCountText: PropTypes.bool,
  showUnlockCountText: PropTypes.bool,
  isPublished: PropTypes.bool,
  tipPosition: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
  ttl: PropTypes.number,
  expiresAtUnix: PropTypes.number,
  likes: PropTypes.number,
  dislikes: PropTypes.number,
  likePercentage: PropTypes.number,
  likePercentageWithSignOrDefault: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  viewCount: PropTypes.string,
  unlockCount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  t: PropTypes.func.isRequired,
};

MessageMeta.defaultProps = {
  showExpires: false,
  showLikes: false,
  showUnlocks: false,
  showViewCount: false,
  showLikesText: false,
  showViewCountText: false,
  showUnlockCountText: false,
  isPublished: false,
  tipPosition: 'top',
  ttl: 0,
  expiresAtUnix: null,
  viewCount: '-',
  unlockCount: '-',
};

const RemainingTime = ({ now, expiresAtUnix }) => {
  const expiresDate = fromUnixTime(expiresAtUnix);
  const duration = intervalToDuration({ start: now, end: expiresDate });
  const diffHours = differenceInHours(expiresDate, now);
  const diffMilliseconds = differenceInMilliseconds(expiresDate, now);
  const hours = formatNumber(Math.floor(diffHours));
  const minutes = formatNumber(duration.minutes);
  const seconds = formatNumber(duration.seconds);
  const isLessThanHour = diffHours < 1;
  const remainingTime =
    diffMilliseconds < 0
      ? '--:--:--'
      : diffHours > 48
        ? '48:00:00+'
        : isLessThanHour
          ? `${minutes}:${seconds}`
          : `${hours}:${minutes}:${seconds}`;

  return (
    <InfoBlock data-value={remainingTime} isLessThanHour={isLessThanHour}>
      <img src={TimerIcon} alt="time left" />
    </InfoBlock>
  );
};

RemainingTime.propTypes = {
  now: PropTypes.instanceOf(Date),
  expiresAtUnix: PropTypes.number,
};

RemainingTime.defaultProps = {
  now: new Date(),
  expiresAtUnix: 0,
};

const StyledMessageMeta = styled.div`
  display: flex;
`;

const InfoBlockWrapper = styled.div`
  & + & {
    margin-left: 8px;
  }
`;

// (2024.03) comment out opacity and font-weight to deal with afterimage issue on OPPO RENO
const InfoBlock = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.75);
  // opacity: ${({ isLessThanHour }) => (isLessThanHour ? 1 : 0.75)};
  // font-weight: ${({ isLessThanHour }) => (isLessThanHour ? 600 : 'normal')};

  &::after {
    content: attr(data-value);
    display: block;
    line-height: 1;
    margin-left: 4px;
  }

  & > img {
    width: 20px;
    height: 20px;
  }

  ${media.mobile`
    // font-weight: ${({ isLessThanHour }) => (isLessThanHour ? 600 : 200)};
    font-size: 12px;
    & > img {
      width: 16px;
      height: 16px;
    }
  `};
`;

const TooltipContentWrapper = styled.div`
  border-radius: 4px;
  padding: 8px 6.5px;
  background: rgba(35, 35, 35, 0.8);
  font-size: 12px;
  color: ${textColor.white};
  white-space: nowrap;
  height: 30px;
  display: grid;
  place-content: center;
  margin-bottom: 4px;
`;

const RatingHint = styled.div`
  min-width: 115px;
  font-size: 9px;
`;

const RatingProgressBar = styled.div`
  display: flex;
  justify-content: flex-end;
  border-radius: 2px;
  width: 100%;
  height: 4px;
  background-color: #646464;
  overflow: hidden;
`;

const RatingProgressValue = styled.div.attrs(props => ({
  style: {
    width: `${props.percentage}%`,
  },
}))`
  background: #00d2be;
`;

const RatingHintInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1px;
`;

const RatingHintCount = styled.div`
  display: flex;
  align-items: center;
  flex-direction: ${({ isReverse }) => (isReverse ? 'row-reverse' : 'row')};

  & > img {
    width: 15px;
    height: 15px;
  }

  & > span {
    margin: 0 1px;
    line-height: 15px;
  }
`;

export default withTranslation()(MessageMeta);
